import { useEffect, useState } from 'react';
import { COMPANION_TURN_STREAM_PATH } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';

/**
 * Atlas live presence (design coLiveConnect, L18257): who is talking right now, from
 * the back end's companion.turn feed: GET /api/aithora/companion/turns/stream (SSE, event
 * "companion.turn", data {resident_id, resident_first, session_state: active|ended}),
 * published by POST /chat for every turn (src/aithora/v3/companion/turns.ts). Bearer
 * auth, so it is read with fetch (EventSource cannot send the header). Presence is never
 * invented: a lost feed says so.
 */
export const COMPANION_TURN_STREAM: string | null = COMPANION_TURN_STREAM_PATH;

export type LiveStatus = 'off' | 'connecting' | 'on' | 'error';
export interface Live {
  status: LiveStatus;
  /** resident key -> last turn time (ms); a session drops off after 90 s without a turn. */
  active: Record<string, number>;
}

export function useCompanionLive(path: string | null = COMPANION_TURN_STREAM): Live {
  const { client, session } = useSession();
  const [live, setLive] = useState<Live>({ status: path ? 'connecting' : 'off', active: {} });

  useEffect(() => {
    if (!path || !session) {
      setLive({ status: 'off', active: {} });
      return;
    }
    const ctrl = new AbortController();
    setLive({ status: 'connecting', active: {} });
    const onTurn = (raw: string) => {
      let d: { resident_first?: string; resident?: string; resident_id?: string; session_state?: string };
      try {
        d = JSON.parse(raw);
      } catch {
        return;
      }
      const who = d.resident_first || d.resident || d.resident_id;
      if (!who) return;
      setLive((p) => {
        const a = { ...p.active };
        if (d.session_state === 'ended') delete a[who];
        else a[who] = Date.now();
        return { status: 'on', active: a };
      });
    };
    (async () => {
      try {
        const res = await client.raw(path, { headers: { accept: 'text/event-stream' }, signal: ctrl.signal });
        // A feed this back end does not serve (404, 501) was never connected, so it is not
        // "lost": the design's "Live feed not connected", never a guess at who is talking.
        if (res.status === 404 || res.status === 501) {
          if (!ctrl.signal.aborted) setLive({ status: 'off', active: {} });
          return;
        }
        if (!res.ok || !res.body) throw new Error(String(res.status));
        setLive((p) => ({ ...p, status: 'on' }));
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          let i: number;
          while ((i = buf.indexOf('\n\n')) >= 0) {
            const frame = buf.slice(0, i);
            buf = buf.slice(i + 2);
            let ev = 'message';
            const data: string[] = [];
            for (const line of frame.split('\n')) {
              if (line.startsWith('event:')) ev = line.slice(6).trim();
              else if (line.startsWith('data:')) data.push(line.slice(5).trim());
            }
            if (data.length && (ev === 'companion.turn' || ev === 'message')) onTurn(data.join('\n'));
          }
        }
        if (!ctrl.signal.aborted) setLive((p) => ({ ...p, status: 'error' }));
      } catch {
        if (!ctrl.signal.aborted) setLive((p) => ({ ...p, status: 'error' }));
      }
    })();
    // a session with no turn for 90 s is no longer "talking now"
    const sweep = window.setInterval(() => {
      setLive((p) => {
        const a = { ...p.active };
        let ch = false;
        Object.keys(a).forEach((k) => {
          if (Date.now() - a[k] > 90000) {
            delete a[k];
            ch = true;
          }
        });
        return ch ? { ...p, active: a } : p;
      });
    }, 15000);
    return () => {
      ctrl.abort();
      window.clearInterval(sweep);
    };
  }, [path, client, session]);

  return live;
}

/** The design's label, title and styles for the live line (coLive*). */
export function liveView(lv: Live) {
  const n = Object.keys(lv.active).length;
  const on = lv.status === 'on';
  const col = on ? (n ? '#1F6B52' : '#54606C') : lv.status === 'error' ? '#8A5A00' : '#54606C';
  return {
    label: on ? (n ? `${n} talking now` : 'No one talking now') : lv.status === 'connecting' ? 'Connecting…' : lv.status === 'error' ? 'Live feed lost' : 'Live feed not connected',
    title: on
      ? 'From the live Atlas conversation feed. A session drops off after 90 seconds without a turn.'
      : 'Presence comes from the live Atlas conversation feed on the server. Until it is connected this list shows recent conversations only, never who is talking now.',
    style: `flex:0 0 auto;display:inline-flex;align-items:center;gap:5px;font-size:14px;font-weight:600;white-space:nowrap;color:${col};`,
    dot: `width:7px;height:7px;border-radius:50%;flex:0 0 7px;${on && n ? 'background:#1F6B52;animation:coLivePulse 1.8s ease-out infinite;' : `background:transparent;border:1.5px solid ${col};box-sizing:border-box;`}`,
  };
}
